import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Banknote, Cable, Cpu, HardDrive, MemoryStick, Plus, Radio, Receipt, Ticket, WifiOff, Zap,
} from "lucide-react";
import { api, bps, errMsg, rp } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatCard } from "@/components/StatCard";
import { TrafficChart } from "@/components/TrafficChart";
import NoRouterNotice from "@/components/NoRouterNotice";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Dashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [iface, setIface] = useState("");
  const [series, setSeries] = useState([]);
  const ifaceRef = useRef("");

  const [rid, setRid] = useState("");
  const [routers, setRouters] = useState([]);
  const [noRouter, setNoRouter] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await api.get(`/dashboard${rid ? `?router_id=${rid}` : ""}`);
      setData(d);
      setNoRouter(false);
      if (!ifaceRef.current && d.interfaces?.length) {
        ifaceRef.current = d.interfaces[0].name;
        setIface(d.interfaces[0].name);
      }
    } catch (e) {
      const st = e?.response?.status;
      const msg = errMsg(e);
      if ((st === 400 || st === 404) && /router/i.test(msg)) { setNoRouter(true); return; }
      toast.error(msg);
    }
  };

  useEffect(() => {
    if (user?.role !== "superadmin") return;
    api.get("/routers").then(({ data }) => setRouters(data)).catch(() => {});
  }, [user?.role]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [rid]);

  useEffect(() => { ifaceRef.current = iface; }, [iface]);

  useEffect(() => {
    if (!iface) return;
    let alive = true;
    const tick = async () => {
      try {
        const { data: t } = await api.get(`/traffic?iface=${encodeURIComponent(iface)}${rid ? `&router_id=${rid}` : ""}`);
        if (!alive) return;
        setSeries((s) => [...s.slice(-29), { t: new Date().toLocaleTimeString("id-ID").slice(0, 8), rx: t.rx_bps, tx: t.tx_bps }]);
      } catch (e) {
        console.error("gagal ambil trafik:", e);
      }
    };
    setSeries([]);
    tick();
    const id = setInterval(tick, 3000);
    return () => { alive = false; clearInterval(id); };
  }, [iface]);

  const r = data?.router;
  const online = r?.online;
  const latest = series[series.length - 1];

  if (noRouter && !data) return <NoRouterNotice role={user?.role} />;

  if (!data) return <div className="text-sm text-muted-foreground" data-testid="dashboard-loading">Memuat data router…</div>;

  return (
    <div className="space-y-5 fade-up sm:space-y-8">
      {user?.role === "superadmin" && routers.length > 0 && (
        <section className="rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid="router-picker-section">
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Pantau Router</p>
            <Select value={rid || "default"} onValueChange={(v) => { setRid(v === "default" ? "" : v); ifaceRef.current = ""; setIface(""); }}>
              <SelectTrigger className="h-10 w-full rounded-full bg-secondary/50 sm:w-[280px]" data-testid="router-picker"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Router utama (otomatis)</SelectItem>
                {routers.map((rt) => (
                  <SelectItem key={rt.id} value={rt.id} data-testid={`router-option-${rt.label}`}>
                    {rt.label} · {rt.host}{rt.link_type ? ` · ${rt.link_type}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {data.owner && (
              <p className="text-xs text-muted-foreground" data-testid="owner-admin">
                Pemilik: <b className="text-foreground">{data.owner.admin_name || "Superadmin"}</b>
                {data.owner.admin_username ? ` (${data.owner.admin_username})` : ""}
              </p>
            )}
          </div>
          {data.owner && (
            <div className="mt-4 grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4" data-testid="owner-params">
              {[
                ["Brand Voucher", data.owner.voucher_brand || "-"],
                ["Harga Voucher Mitra", rp(data.owner.voucher_price_mitra)],
                ["Harga Voucher User", rp(data.owner.voucher_price_user)],
                ["Harga FTTH", rp(data.owner.ftth_price)],
                ["Nama WiFi", data.owner.wifi_name || "-"],
                ["Paket Voucher", `${(data.owner.packages || []).length} paket`],
                ["Paket FTTH", `${(data.owner.ftth_packages || []).length} paket`],
                ["WA Admin", data.owner.admin_wa || "-"],
              ].map(([l, v]) => (
                <div key={l} className="rounded-xl border border-border bg-secondary/30 px-3 py-2 sm:px-4 sm:py-3">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{l}</p>
                  <p className="mt-1 truncate font-mono text-xs sm:text-sm">{v}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Router status */}
      <section className="rounded-2xl border border-border bg-card p-4 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3 sm:gap-6">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Status Router</p>
            <h2 className="mt-1 font-display text-lg font-semibold tracking-tight sm:mt-2 sm:text-2xl" data-testid="router-label">
              {r?.label} {r?.demo ? <span className="ml-2 rounded-full border border-violet-500/40 px-2 py-0.5 text-[10px] uppercase tracking-widest text-violet-400">demo</span> : null}
            </h2>
            <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-[11px] sm:gap-x-8 sm:text-xs text-muted-foreground sm:grid-cols-4">
              <div>identity<div className="text-foreground">{r?.identity || "-"}</div></div>
              <div>board<div className="text-foreground">{r?.board_name || "-"}</div></div>
              <div>routeros<div className="text-foreground">{r?.version || "-"}</div></div>
              <div>serial<div className="text-foreground">{r?.serial_number || "-"}</div></div>
              <div>uptime<div className="text-foreground" data-testid="router-uptime">{r?.uptime || "-"}</div></div>
              <div>waktu sistem<div className="text-foreground">{new Date().toLocaleString("id-ID")}</div></div>
            </div>
          </div>
          <div
            data-testid="router-status"
            className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] font-medium sm:px-4 sm:py-2 sm:text-xs ${
              online ? "border-emerald-500/40 text-emerald-400" : "border-red-500/40 text-red-400"
            }`}
          >
            {online ? <Zap className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
            {online ? "Router Online" : "Router Offline"}
          </div>
        </div>

        {online ? (
          <div className="mt-5 grid grid-cols-3 gap-2" data-testid="router-meters">
            {[
              ["CPU", r.cpu_load, `${r.cpu_count || 1} core`, Cpu, "gauge-cpu"],
              ["RAM", r.memory_used_pct, "memory", MemoryStick, "gauge-memory"],
              ["Disk", r.disk_used_pct, "storage", HardDrive, "gauge-disk"],
            ].map(([label, val, sub, Icon, tid]) => {
              const v = Math.min(Math.max(Number(val) || 0, 0), 100);
              const tone = v < 60 ? "bg-emerald-400" : v < 85 ? "bg-amber-400" : "bg-rose-500";
              const text = v < 60 ? "text-emerald-400" : v < 85 ? "text-amber-400" : "text-rose-400";
              return (
                <div key={label} data-testid={tid} className="rounded-lg border border-border px-2 py-1.5">
                  <div className="flex items-center justify-between gap-1">
                    <span className="flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
                      <Icon className="h-3 w-3" />{label}
                    </span>
                    <span className={`font-mono text-xs font-semibold ${text}`}>{Math.round(v)}%</span>
                  </div>
                  <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-secondary">
                    <div className={`h-full rounded-full ${tone} transition-all duration-500`} style={{ width: `${v}%` }} />
                  </div>
                  <p className="mt-0.5 truncate font-mono text-[9px] text-muted-foreground">{sub}</p>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-6 rounded-xl border border-red-500/30 bg-red-500/5 p-5 text-sm text-red-400" data-testid="router-offline-msg">
            Koneksi ke Mikrotik terputus. Periksa IP/port/kredensial API di menu Setting. {r?.error ? <span className="font-mono text-xs">({r.error})</span> : null}
          </div>
        )}
      </section>

      {/* Revenue */}
      <section>
        <p className="mb-3 text-[10px] uppercase tracking-[0.25em] text-muted-foreground sm:mb-4">Pendapatan</p>
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          <StatCard testId="stat-revenue-daily" label="Voucher Hari Ini" value={rp(data.revenue.daily_voucher ?? 0)} icon={Ticket} hint={`${data.revenue.daily_vouchers} voucher dibuat (semua akun)`} accent="from-sky-400 to-cyan-400" />
          <StatCard testId="stat-ftth-daily" label="FTTH Hari Ini" value={rp(data.revenue.daily_ftth ?? 0)} icon={Receipt} accent="from-emerald-300 to-teal-400" />
          <StatCard testId="stat-voucher-monthly" label="Voucher Bulan Ini" value={rp(data.revenue.monthly_voucher ?? 0)} icon={Ticket} hint={`${data.revenue.monthly_vouchers} voucher dibuat (semua akun)`} accent="from-violet-400 to-indigo-400" />
          <StatCard testId="stat-revenue-monthly" label="FTTH Bulan Ini" value={rp(data.revenue.monthly_ftth ?? 0)} icon={Banknote} accent="from-fuchsia-400 to-rose-400" />
        </div>
      </section>

      {/* Ringkasan tagihan FTTH */}
      {data.ftth_summary && (
        <section>
          <p className="mb-3 text-[10px] uppercase tracking-[0.25em] text-muted-foreground sm:mb-4">Tagihan FTTH Bulan Ini</p>
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            <StatCard testId="stat-ftth-unpaid" label="Belum Bayar" value={data.ftth_summary.unpaid_count} icon={Receipt} hint={`dari ${data.ftth_summary.customers} pelanggan`} accent="from-amber-300 to-orange-400" onClick={() => nav("/pelanggan?tab=tagihan")} />
            <StatCard testId="stat-ftth-outstanding" label="Total Tunggakan" value={rp(data.ftth_summary.outstanding)} icon={Banknote} accent="from-orange-300 to-rose-400" onClick={() => nav("/pelanggan?tab=tagihan")} />
            <StatCard testId="stat-ftth-paid" label="Sudah Lunas" value={data.ftth_summary.paid_count} icon={Banknote} accent="from-teal-300 to-emerald-400" onClick={() => nav("/pelanggan")} />
            <StatCard testId="stat-ftth-isolated" label="Terisolir" value={data.ftth_summary.isolated} icon={Cable} accent="from-rose-300 to-violet-400" onClick={() => nav("/pelanggan?tab=isolir")} />
          </div>
        </section>
      )}

      {/* Hotspot + PPPoE */}
      <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-gradient-to-br from-sky-400/25 via-card to-cyan-400/15 p-4 sm:p-6">
          <div className="flex items-center gap-2"><Radio className="h-4 w-4 text-sky-400" /><p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Panel Hotspot</p></div>
          <div className="mt-4 grid grid-cols-3 gap-2 sm:mt-5 sm:gap-3">
            <button onClick={() => nav("/hotspot?tab=reset")} data-testid="hotspot-active-count" className="rounded-xl border border-border/60 bg-card/60 p-2.5 text-left sm:p-3 transition-colors duration-200 hover:border-sky-400/60">
              <p className="font-mono text-xl sm:text-2xl">{data.hotspot.active}</p><p className="text-[10px] leading-snug text-muted-foreground sm:text-[11px]">User aktif online</p>
            </button>
            <button onClick={() => nav("/laporan?tab=vouchers")} data-testid="hotspot-generated-count" className="rounded-xl border border-border/60 bg-card/60 p-2.5 text-left sm:p-3 transition-colors duration-200 hover:border-violet-400/60">
              <p className="font-mono text-xl sm:text-2xl">{data.hotspot.generated}</p><p className="text-[10px] leading-snug text-muted-foreground sm:text-[11px]">Voucher tergenerate</p>
            </button>
            <button onClick={() => nav("/hotspot?tab=users")} data-testid="hotspot-total-count" className="rounded-xl border border-border/60 bg-card/60 p-2.5 text-left sm:p-3 transition-colors duration-200 hover:border-fuchsia-400/60">
              <p className="font-mono text-xl sm:text-2xl">{data.hotspot.total_users}</p><p className="text-[10px] leading-snug text-muted-foreground sm:text-[11px]">Total voucher di router</p>
            </button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2 sm:mt-6">
            <Button asChild size="sm" className="rounded-full bg-gradient-to-r from-sky-400 to-violet-500 text-white"><Link to="/hotspot?tab=add" data-testid="quick-add-hotspot"><Plus className="mr-1 h-3.5 w-3.5" /> Tambah User Hotspot</Link></Button>
            <Button asChild size="sm" variant="outline" className="rounded-full"><Link to="/hotspot?tab=generate" data-testid="quick-generate-hotspot">Generate Massal</Link></Button>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-gradient-to-br from-violet-400/25 via-card to-fuchsia-400/15 p-4 sm:p-6">
          <div className="flex items-center gap-2"><Cable className="h-4 w-4 text-violet-400" /><p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Panel PPPoE</p></div>
          <div className="mt-4 grid grid-cols-3 gap-2 sm:mt-5 sm:gap-3">
            <button onClick={() => nav("/ftth?tab=aktif")} data-testid="ppp-active-count" className="rounded-xl border border-border/60 bg-card/60 p-2.5 text-left sm:p-3 transition-colors duration-200 hover:border-emerald-400/60">
              <p className="font-mono text-xl text-emerald-400 sm:text-2xl">{data.pppoe.active}</p><p className="text-[10px] leading-snug text-muted-foreground sm:text-[11px]">Aktif</p>
            </button>
            <button onClick={() => nav("/ftth?tab=inaktif")} data-testid="ppp-inactive-count" className="rounded-xl border border-border/60 bg-card/60 p-2.5 text-left sm:p-3 transition-colors duration-200 hover:border-rose-400/60">
              <p className="font-mono text-xl text-rose-400 sm:text-2xl">{data.pppoe.inactive}</p><p className="text-[10px] leading-snug text-muted-foreground sm:text-[11px]">Inaktif</p>
            </button>
            <button onClick={() => nav("/ftth?tab=secret")} data-testid="ppp-secret-count" className="rounded-xl border border-border/60 bg-card/60 p-2.5 text-left sm:p-3 transition-colors duration-200 hover:border-violet-400/60">
              <p className="font-mono text-xl sm:text-2xl">{data.pppoe.secrets}</p><p className="text-[10px] leading-snug text-muted-foreground sm:text-[11px]">Secret terdaftar</p>
            </button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2 sm:mt-6">
            <Button asChild size="sm" className="rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white"><Link to="/ftth?tab=secret" data-testid="quick-add-secret"><Plus className="mr-1 h-3.5 w-3.5" /> Add Secret PPPoE</Link></Button>
            <Button asChild size="sm" variant="outline" className="rounded-full"><Link to="/ftth?tab=profil" data-testid="quick-add-profile">Add Profil PPPoE</Link></Button>
          </div>
        </div>
      </section>

      {/* Traffic */}
      <section className="rounded-2xl border border-border bg-card p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 sm:gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Traffic Interface (Real-time)</p>
            <p className="mt-2 font-mono text-xs text-muted-foreground">
              <span className="text-cyan-400">RX {bps(latest?.rx || 0)}</span> · <span className="text-violet-400">TX {bps(latest?.tx || 0)}</span>
            </p>
          </div>
          <Select value={iface} onValueChange={setIface}>
            <SelectTrigger className="w-full rounded-full sm:w-[200px]" data-testid="iface-select"><SelectValue placeholder="Pilih interface" /></SelectTrigger>
            <SelectContent>
              {(data.interfaces || []).map((i) => (<SelectItem key={i.name} value={i.name}>{i.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="mt-4 sm:mt-6"><TrafficChart data={series} /></div>
      </section>
    </div>
  );
}
